"use client";

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Package, Loader2 } from "lucide-react";
import Pagination from "../Common/Pagination";

const TRANSACTION_TYPE_LABELS = {
  purchase: "Purchase",
  issue: "Issue",
  adjustment: "Adjustment",
  hold: "Hold",
  unhold: "Unhold",
};

const TYPE_BADGE_CLASS = {
  purchase: "bg-emerald-100 text-emerald-800 border-emerald-200",
  issue: "bg-blue-100 text-blue-800 border-blue-200",
  adjustment: "bg-amber-100 text-amber-900 border-amber-200",
  hold: "bg-orange-100 text-orange-900 border-orange-200",
  unhold: "bg-slate-100 text-slate-800 border-slate-200",
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

const SOURCE_LABELS = {
  web: "Web",
  app: "App",
};

const SOURCE_BADGE_CLASS = {
  web: "bg-sky-100 text-sky-800 border-sky-200",
  app: "bg-teal-100 text-teal-800 border-teal-200",
};

export default function SchoolInventoryActivity() {
  const { schoolId } = useParams();
  const navigate = useNavigate();

  const [school, setSchool] = useState(null);
  const [schoolLoading, setSchoolLoading] = useState(true);

  const [txRange, setTxRange] = useState("24h");
  const [txSummary, setTxSummary] = useState(null);
  const [txLoading, setTxLoading] = useState(true);
  const [txError, setTxError] = useState("");

  const [txRows, setTxRows] = useState([]);
  const [txRowsLoading, setTxRowsLoading] = useState(true);
  const [txRowsError, setTxRowsError] = useState("");
  const [txRowsPage, setTxRowsPage] = useState(1);
  const [txRowsLimit, setTxRowsLimit] = useState(10);
  const [txRowsTotal, setTxRowsTotal] = useState(0);
  const [txTypeFilter, setTxTypeFilter] = useState(null);
  const [txBulkFilter, setTxBulkFilter] = useState("all");
  const [txSourceFilter, setTxSourceFilter] = useState("all");

  useEffect(() => {
    const fetchSchool = async () => {
      if (!schoolId) return;
      setSchoolLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axiosInstance.get(API_ENDPOINTS.schoolsStats, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const schools = res.data?.data?.schools || [];
        const found = schools.find((s) => String(s._id) === String(schoolId));
        setSchool(found || null);
      } catch {
        setSchool(null);
      } finally {
        setSchoolLoading(false);
      }
    };
    fetchSchool();
  }, [schoolId]);

  const fetchTransactionsSummary = useCallback(async () => {
    if (!schoolId) return;
    setTxLoading(true);
    setTxError("");
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.inventoryTransactionsSummary(schoolId, txRange));
      setTxSummary(res.data?.data || null);
    } catch (e) {
      setTxError(e.response?.data?.message || "Failed to load transaction summary");
      setTxSummary(null);
    } finally {
      setTxLoading(false);
    }
  }, [schoolId, txRange]);

  useEffect(() => {
    fetchTransactionsSummary();
  }, [fetchTransactionsSummary]);

  const fetchTransactionRows = useCallback(async () => {
    if (!schoolId) return;
    setTxRowsLoading(true);
    setTxRowsError("");
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.inventoryTransactions, {
        params: {
          schoolId,
          range: txRange,
          page: txRowsPage,
          limit: txRowsLimit,
          paginate: "true",
          ...(txTypeFilter ? { type: txTypeFilter } : {}),
          ...(txBulkFilter !== "all" ? { bulk: txBulkFilter === "bulk" ? "true" : "false" } : {}),
          ...(txSourceFilter !== "all" ? { platform: txSourceFilter } : {}),
        },
      });
      const body = res.data || {};
      setTxRows(body.data || []);
      setTxRowsTotal(typeof body.count === "number" ? body.count : (body.data || []).length);
    } catch (e) {
      setTxRowsError(e.response?.data?.message || "Failed to load recent transactions");
      setTxRows([]);
      setTxRowsTotal(0);
    } finally {
      setTxRowsLoading(false);
    }
  }, [schoolId, txRange, txRowsPage, txRowsLimit, txTypeFilter, txBulkFilter, txSourceFilter]);

  useEffect(() => {
    fetchTransactionRows();
  }, [fetchTransactionRows]);

  useEffect(() => {
    setTxRowsPage(1);
  }, [txRange, txTypeFilter, txBulkFilter, txSourceFilter]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Activity</h1>
          <p className="text-sm text-gray-500">
            {schoolLoading ? "Loading customer..." : school ? `${school.name} · ${school.domain}` : "Customer"}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/dashboard/schools/${schoolId}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Customer Details
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
              <Package className="h-4 w-4 text-blue-600" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Inventory Activity</h2>
              <p className="text-xs text-gray-500">
                Stock movement volume for this customer. Item-level detail stays inside their own dashboard.
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            {["24h", "7d"].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setTxRange(r)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  txRange === r
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {r === "24h" ? "Last 24h" : "Last 7 days"}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5">
          {txError && (
            <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {txError}
            </div>
          )}

          {txLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : txSummary ? (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setTxTypeFilter(null)}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                  txTypeFilter === null
                    ? "border-blue-300 bg-blue-100"
                    : "border-blue-100 bg-blue-50 hover:bg-blue-100/60"
                }`}
              >
                <div className="text-sm text-blue-700">Total transactions</div>
                <div className="text-3xl font-bold text-blue-900">{txSummary.total}</div>
              </button>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                {Object.entries(txSummary.byType || {}).map(([type, count]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setTxTypeFilter((prev) => (prev === type ? null : type))}
                    className={`rounded-lg border p-3 text-center transition-colors ${
                      txTypeFilter === type
                        ? "border-blue-300 bg-blue-50 ring-1 ring-blue-300"
                        : "border-gray-100 bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="text-2xl font-semibold text-gray-900">{count}</div>
                    <div className="mt-1 text-xs text-gray-500">{TRANSACTION_TYPE_LABELS[type] || type}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400">Click on a card to filter Recent Transactions by that type.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Package className="h-10 w-10 text-gray-300" />
              <p className="mt-2 text-sm">No inventory activity in this range.</p>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Recent Transactions</h2>
            <p className="text-xs text-gray-500">
              {txTypeFilter ? `${TRANSACTION_TYPE_LABELS[txTypeFilter] || txTypeFilter} only` : "All types"} in the{" "}
              {txRange === "7d" ? "last 7 days" : "last 24h"} for this customer
              {txRowsTotal ? ` (${txRowsTotal} total)` : ""}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1">
              {[
                { key: "all", label: "All" },
                { key: "bulk", label: "Bulk" },
                { key: "normal", label: "Normal" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setTxBulkFilter(opt.key)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    txBulkFilter === opt.key
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              {[
                { key: "all", label: "All Sources" },
                { key: "web", label: "Web" },
                { key: "app", label: "App" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setTxSourceFilter(opt.key)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    txSourceFilter === opt.key
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {txTypeFilter && (
              <button
                type="button"
                onClick={() => setTxTypeFilter(null)}
                className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200"
              >
                Clear type filter
              </button>
            )}
          </div>
        </div>

        <div className="p-5">
          {txRowsError && (
            <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {txRowsError}
            </div>
          )}

          {txRowsLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : txRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <th className="pb-2 pr-4 font-medium">Time</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Item</th>
                    <th className="pb-2 pr-4 font-medium">Source</th>
                    <th className="pb-2 text-right font-medium">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {txRows.map((row) => (
                    <tr key={row._id} className="border-b border-gray-50 last:border-0">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{formatWhen(row.created_at)}</td>
                      <td className="py-2.5 pr-4">
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                            TYPE_BADGE_CLASS[row.type] || "bg-gray-100 text-gray-800 border-gray-200"
                          }`}
                        >
                          {TRANSACTION_TYPE_LABELS[row.type] || row.type}
                        </span>
                        {row.metadata?.bulk && (
                          <span className="ml-1.5 inline-flex rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
                            Bulk
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-gray-500">
                        {row.inventory?.inventory_id ? `Item #${row.inventory.inventory_id}` : "—"}
                      </td>
                      <td className="py-2.5 pr-4">
                        {row.platform ? (
                          <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                              SOURCE_BADGE_CLASS[row.platform] || "bg-gray-100 text-gray-800 border-gray-200"
                            }`}
                          >
                            {SOURCE_LABELS[row.platform] || row.platform}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-gray-800">{row.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Package className="h-10 w-10 text-gray-300" />
              <p className="mt-2 text-sm">No transactions found.</p>
            </div>
          )}
        </div>

        {!txRowsLoading && txRows.length > 0 && (
          <Pagination
            currentPage={txRowsPage}
            totalItems={txRowsTotal}
            itemsPerPage={txRowsLimit}
            currentPageCount={txRows.length}
            onPageChange={setTxRowsPage}
            onItemsPerPageChange={(v) => {
              setTxRowsLimit(v);
              setTxRowsPage(1);
            }}
            pageSizeOptions={[10, 25, 50]}
            itemLabel="transactions"
          />
        )}
      </div>
    </div>
  );
}
